import { createContext, useMemo } from 'react'
import { useTeam } from 'app/frontend/base/config/context/team'
import RoleBlock from './RoleBlock'
import {
  useTeamCharacterDetails,
  useTeamUsers,
} from 'app/frontend/base/config/queries'
import { useGroupedTeamMembers, useMembersByRole } from '../hooks'
import RosterStats from './RosterStats'
import { cn } from '@/lib/utils'
import { useBreakpoint } from 'app/frontend/base/hooks/useBreakpoint'

type Props = {
  members: wowaudit.TeamMembership[]
  compact?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export const RosterSetupContext = createContext<{
  mains: wowaudit.TeamMembership[]
  alts: wowaudit.TeamMembership[]
  reserves: wowaudit.TeamMembership[]
  membersWithOffspecs: {
    member: wowaudit.TeamMembership
    offspecRole: string
  }[]
  mainsByRole: Record<string, wowaudit.TeamMembership[]>
  altsByRole: Record<string, wowaudit.TeamMembership[]>
  reservesByRole: Record<string, wowaudit.TeamMembership[]>
  offSpecsByRole: Record<
    string,
    { member: wowaudit.TeamMembership; offspecRole: string }[]
  >
  charactersById?: Record<string, wowaudit.CharacterDetails>
  mainByUser?: Record<string, wowaudit.TeamMembership>
  usersById?: Record<string, wowaudit.UserReference>
  userIdsWithDuplicateMains: number[]
  sortedMembers: wowaudit.TeamMembership[]
}>({
  mains: [],
  alts: [],
  reserves: [],
  membersWithOffspecs: [],
  mainsByRole: {},
  altsByRole: {},
  reservesByRole: {},
  offSpecsByRole: {},
  charactersById: {},
  mainByUser: {},
  userIdsWithDuplicateMains: [],
  sortedMembers: [],
})

const RosterSetup = ({ members, compact = false, className }: Props) => {
  const { team } = useTeam()
  const breakpoint = useBreakpoint()

  const sortedMembers = useMemo(() => {
    return members.sort((a, b) => {
      return `${a.characterReference.characterClass}-${a.characterReference.name}`.localeCompare(
        `${b.characterReference.characterClass}-${b.characterReference.name}`,
      )
    })
  }, [members])

  const {
    mains,
    mainsByRole,
    reserves,
    reservesByRole,
    alts,
    altsByRole,
    membersWithOffspecs,
    offSpecsByRole,
  } = useMembersByRole(sortedMembers)

  const { mainByUser, userIdsWithDuplicateMains } = useGroupedTeamMembers()

  const { data: memberDetails } = useTeamCharacterDetails(
    team.id,
    sortedMembers?.map((member) => member.characterReference.id),
  )

  const charactersById = useMemo(
    () =>
      memberDetails?.reduce((acc, character) => {
        acc[character.id.toString()] = character
        return acc
      }, {} as Record<string, wowaudit.CharacterDetails>),
    [memberDetails],
  )

  const { data: teamUsers } = useTeamUsers(team.id)
  const usersById = useMemo(
    () =>
      teamUsers?.reduce((acc, user) => {
        acc[user.id.toString()] = user
        return acc
      }, {} as Record<string, wowaudit.UserReference>),
    [teamUsers],
  )

  return (
    <RosterSetupContext.Provider
      value={{
        mains,
        mainsByRole,
        reserves,
        reservesByRole,
        alts,
        altsByRole,
        membersWithOffspecs,
        offSpecsByRole,
        charactersById,
        mainByUser,
        usersById,
        userIdsWithDuplicateMains: userIdsWithDuplicateMains ?? [],
        sortedMembers,
      }}
    >
      <div className='flex gap-4 flex-col xl:flex-row'>
        <div className='flex-1'>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
            {['sm', 'md'].includes(breakpoint) ? (
              <>
                <div className='flex flex-col gap-3'>
                  <RoleBlock
                    className={cn(className, 'grow')}
                    compact={compact}
                    role={'tank'}
                  />
                  <RoleBlock
                    className={cn(className, 'grow')}
                    compact={compact}
                    role={'ranged'}
                  />
                </div>
                <div className='flex flex-col gap-3'>
                  <RoleBlock
                    className={cn(className, 'grow')}
                    compact={compact}
                    role={'heal'}
                  />
                  <RoleBlock
                    className={cn(className, 'grow')}
                    compact={compact}
                    role={'melee'}
                  />
                </div>
              </>
            ) : (
              <>
                <div className='flex flex-col gap-3'>
                  <RoleBlock
                    className={cn(className, 'grow')}
                    compact={compact}
                    role={'tank'}
                  />
                  <RoleBlock
                    className={cn(className, 'grow')}
                    compact={compact}
                    role={'heal'}
                  />
                </div>
                <RoleBlock
                  className={className}
                  compact={compact}
                  role={'melee'}
                />
                <RoleBlock
                  className={className}
                  compact={compact}
                  role={'ranged'}
                />
              </>
            )}
          </div>
        </div>

        {!compact && <RosterStats />}
      </div>
    </RosterSetupContext.Provider>
  )
}

export default RosterSetup
